import { Div, Dropdown, FormStatus, P, Section } from '@dnb/eufemia';
import type { AggregationProviderResponseDto } from '@portals/shared/portal/AggregationProviderDto';
import type { ApiDto } from '@portals/shared/portal/ApiDto';
import type { ApiEnvironmentDto } from '@portals/shared/portal/ApiEnvironmentDto';
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { getApiMetadata } from '@/api/apiMetadata';
import Container from '@/components/Container';
import DataTable, { type Column } from '@/components/DataTable';
import LoadingModal from '@/components/LoadingModal';
import Page from '@/components/Page';
import useSWRExpect404 from '@/hooks/useSWRExpect404';

import style from './AggregationProviders.module.css';

const EXTERNAL_ACCOUNTS_SLUG = 'external-accounts';
type DropdownOption<T> = { selected_key: T; content: string };

interface ProviderRow {
  id: string;
  name: string;
  country: string;
  paymentEnabled: string;
  domesticPaymentMethods: string;
  internationalPaymentMethods: string;
  operationalStatus: string;
}

const COLUMNS: Column<ProviderRow>[] = [
  { header: 'Name', attribute: 'name' },
  { header: 'Id', attribute: 'id' },
  { header: 'Country', attribute: 'country' },
  { header: 'Payments Enabled', attribute: 'paymentEnabled' },
  { header: 'Domestic Payment Methods', attribute: 'domesticPaymentMethods' },
  {
    header: 'International Payment Methods',
    attribute: 'internationalPaymentMethods',
  },
  { header: 'Operational Status', attribute: 'operationalStatus' },
];

const fetchMetadata = async (environments: string[]) => {
  const metadata = await Promise.all(
    environments.map(async (environment) => {
      try {
        const response = await getApiMetadata<AggregationProviderResponseDto>(EXTERNAL_ACCOUNTS_SLUG, environment);
        return { [environment]: response };
      } catch {
        return {};
      }
    }),
  );
  return metadata.reduce((acc, curr) => Object.assign(acc, curr), {});
};

export default function AggregationProviders(): JSX.Element {
  const [searchParams, setSearchParam] = useSearchParams();
  const swrExpect404 = useSWRExpect404();

  const activeEnvironment = searchParams.get('environment') ?? undefined;
  const activeChannel = searchParams.get('channel') ?? undefined;
  const activeConfig = searchParams.get('config') ?? undefined;
  const [error, setError] = useState<string | undefined>();

  const { data: apis, isValidating: loadingApis } = useSWR<ApiDto[]>('/api', swrExpect404);

  const [environmentOptions, setEnvironmentOptions] = useState<DropdownOption<string>[]>();
  const environmentSlugs = useMemo(
    () => environmentOptions?.map((env) => env.selected_key) ?? [],
    [environmentOptions],
  );

  const { data: environmentMetadata, isValidating: loadingEnvironmentMetadata } = useSWR(
    environmentSlugs.length > 0 ? [environmentSlugs] : null,
    () => fetchMetadata(environmentSlugs),
  );

  const environmentChannelKeys = useMemo(() => {
    if (environmentMetadata && activeEnvironment && environmentMetadata[activeEnvironment]) {
      return Object.keys(environmentMetadata[activeEnvironment]);
    }
    return [];
  }, [environmentMetadata, activeEnvironment]);

  const selectedProviders = useMemo(() => {
    if (environmentMetadata && activeEnvironment && activeChannel) {
      return environmentMetadata[activeEnvironment]?.[activeChannel];
    }
    return undefined;
  }, [environmentMetadata, activeEnvironment, activeChannel]);

  const configKeys = useMemo(() => {
    if (selectedProviders) {
      return Object.keys(selectedProviders);
    }
    return [];
  }, [selectedProviders]);

  const updateSearchParam = useCallback(
    (key: string, value: string | undefined) => {
      setSearchParam((prev) => {
        const params = new URLSearchParams(prev);
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
        return params;
      });
    },
    [setSearchParam],
  );

  useEffect(() => {
    if (apis && !environmentOptions) {
      const api = apis.find(({ slug }) => slug === EXTERNAL_ACCOUNTS_SLUG);
      if (!api) {
        setError('External Accounts API not found. Please contact support.');
        return;
      }

      const environmentSlugNameMap = api.environments.map((environment: ApiEnvironmentDto) => ({
        selected_key: environment.slug,
        content: environment.name,
      }));

      setEnvironmentOptions(environmentSlugNameMap);

      // Set default environment if not set
      if (!activeEnvironment) {
        const defaultEnvironment =
          api.environments.find(({ isDefault }) => isDefault)?.slug ?? api.environments[0]!.slug;
        updateSearchParam('environment', defaultEnvironment);
      }
    }
  }, [activeEnvironment, apis, updateSearchParam, environmentOptions]);

  // Set default channel if not set
  useEffect(() => {
    if (environmentChannelKeys.length > 0 && !activeChannel) {
      updateSearchParam('channel', environmentChannelKeys[0]);
    }
  }, [environmentChannelKeys, activeChannel, updateSearchParam]);

  // Set default config if not set
  useEffect(() => {
    if (configKeys.length > 0 && !activeConfig) {
      // Prefer LIVE if present
      if (configKeys.includes('LIVE')) {
        updateSearchParam('config', 'LIVE');
      } else {
        updateSearchParam('config', configKeys[0]);
      }
    }
  }, [configKeys, activeConfig, updateSearchParam]);

  const rows = useMemo(() => {
    if (!selectedProviders || !activeConfig || !selectedProviders[activeConfig]) {
      setError('No provider data was for the selected environment. Please contact support.');
      return [];
    }
    setError(undefined);
    return selectedProviders[activeConfig].map<ProviderRow>(
      ({ countryCode, id, name, paymentMethods, paymentEnabled, status }) => ({
        id,
        name,
        country: countryCode,
        paymentEnabled: paymentEnabled ? 'Yes' : 'No',
        domesticPaymentMethods: paymentMethods?.domestic?.executionTypes.join(',') ?? '',
        internationalPaymentMethods: paymentMethods?.international?.executionTypes.join(',') ?? '',
        operationalStatus: status.syncMode === 'Full' ? 'Fully operational' : 'Partially operational',
      }),
    );
  }, [activeConfig, selectedProviders]);

  if (loadingApis || loadingEnvironmentMetadata) {
    return <LoadingModal />;
  }

  return (
    <Page
      description={
        <Container size="small">
          <P>
            Provider Config is a list of banks supported by Aggregation Data. This list is specific to the selected
            service and environment.
          </P>
          <P top="small">
            For each provider, we supply information about the type of payments that the provider supports, both for
            domestic as well as international. The various payments methods are Normal, Instant and SpecificDate.
          </P>
          <P top="small">
            If Sync is full, it means that aggregation can perform unattended account details requests on a user&apos;s
            behalf, without needing strong costumer authentication.
          </P>
        </Container>
      }
      spacing={error ? 'medium' : 'xx-large'}
      subtitle="Providers"
      title="Supported bank providers"
    >
      <Container centered>
        {error && (
          <FormStatus
            bottom="medium"
            id="provider-error"
            shellSpace="0"
            show
            size="large"
            stretch
            text={error}
            variant="outlined"
          />
        )}
        <Section style_type="white">
          <DataTable
            barContent={
              <Div className={style['Filters']}>
                <Dropdown
                  className={style['dnb-dropdown']}
                  data={environmentChannelKeys}
                  disabled={environmentChannelKeys?.length === 0}
                  label="Channel"
                  label_direction="vertical"
                  on_change={({ data }) => updateSearchParam('channel', data)}
                  title="Select Channel"
                  value={activeChannel && environmentChannelKeys?.indexOf(activeChannel)}
                />

                <Dropdown
                  className={style['dnb-dropdown']}
                  data={configKeys}
                  disabled={configKeys?.length === 0}
                  label="Config"
                  label_direction="vertical"
                  on_change={({ data }) => updateSearchParam('config', data)}
                  title="Select Config"
                  value={activeConfig && configKeys?.indexOf(activeConfig)}
                />

                <Dropdown
                  className={style['dnb-dropdown']}
                  data={environmentOptions}
                  disabled={environmentOptions?.length === 0}
                  label="Environment"
                  label_direction="vertical"
                  on_change={({ data }) => updateSearchParam('environment', data.selected_key)}
                  title="Select environment"
                  value={
                    activeEnvironment && environmentOptions?.findIndex((env) => env.selected_key === activeEnvironment)
                  }
                />
              </Div>
            }
            columns={COLUMNS}
            data={rows}
            defaultSortKey="name"
            filterBy={['name', 'id', 'country']}
            searchTopMargin="large"
            skeleton={loadingEnvironmentMetadata}
          />
        </Section>
      </Container>
    </Page>
  );
}
